import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ExamTemplateFormDialogComponent } from 'src/app/components/main/employees/rrhh/exam-template-form-dialog/exam-template-form-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamTemplate } from 'src/app/models/exam_template.model';
import { ApiService, UtilsService } from 'src/app/services';

type ExamTemplateRow = ExamTemplate & {
  questions_count_label: string;
};

@Component({
  selector: 'app-exam-templates-list',
  templateUrl: './exam-templates-list.component.html',
  styleUrl: './exam-templates-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class ExamTemplatesListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);

  dataSource = new MatTableDataSource<ExamTemplateRow>([]);

  displayedColumns: string[] = ['name', 'description', 'questions_count', 'actions'];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenSearchFormControl();
  }

  openCreateDialog(): void {
    const dialog_ref = this.mat_dialog.open(ExamTemplateFormDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: { mode: 'create' },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
          this.fetchData();
        }
      });
  }

  openEditDialog(exam_template: ExamTemplate): void {
    const dialog_ref = this.mat_dialog.open(ExamTemplateFormDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: { mode: 'edit', exam_template_id: exam_template.id },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  deleteExamTemplate(exam_template: ExamTemplate): void {
    if (!confirm(`¿Eliminar la plantilla "${exam_template.name}"?`)) {
      return;
    }

    this.api_service
      .deleteExamTemplate(exam_template.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          const current_page = Number(this.filterForm.get('page')?.value ?? 1);
          if (this.dataSource.data.length === 1 && current_page > 1) {
            this.filterForm.patchValue({ page: current_page - 1 }, { emitEvent: false });
          }
          this.fetchData();
          this.snack_bar.open('Plantilla eliminada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'name:asc',
      with: 'exam_template_items',
    };

    if (search_term) {
      params.search = search_term;
    }

    this.api_service
      .getExamTemplatesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<ExamTemplate>): void => {
          this.dataSource.data = response.data.map(exam_template => this.mapExamTemplateRow(exam_template));
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private mapExamTemplateRow(exam_template: ExamTemplate): ExamTemplateRow {
    const questions_count = exam_template.exam_template_items?.length ?? 0;

    return {
      ...exam_template,
      questions_count_label: String(questions_count),
    };
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }
}
